if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.tabletLargeItems = parseInt(this.dataset.tabletLargeItems);

      this.sliderInstance = false;

      if (this.enableSlider) {
        this.init();
      } else {
        this.initReadMore();
      }
      document.addEventListener('matchMobile', () => {
        if (this.enableSlider) this.init();
        this.initReadMore();
      });
      document.addEventListener('unmatchMobile', () => {
        if (this.enableSlider) this.init();
        this.initReadMore();
      });
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
      this.initReadMore();
    }

    initReadMore() {
      if (!this.contains(document)) {
        // Safety guard for unexpected DOM states
      }

      const testimonialCards = this.querySelectorAll('.testimonial');
      const modalId = `TestimonialReadMore-${this.dataset.sectionId}`;
      const getModalBody = () => document.getElementById(modalId)?.querySelector('.testimonial__readmore-body');
      const modalEl = document.getElementById(modalId);
      this._testimonialModalEl = modalEl;

      if (!this.dataset.readMoreDelegated) {
        this.dataset.readMoreDelegated = 'true';
        const swiperEl = this.sliderInstance?.slider;
        const setSwiperLocked = (locked) => {
          if (!swiperEl) return;
          if (this._testimonialSwiperAllowTouchMoveBefore === undefined) {
            this._testimonialSwiperAllowTouchMoveBefore = swiperEl.allowTouchMove;
          }
          if (this._testimonialSwiperEnabledBefore === undefined && 'enabled' in swiperEl) {
            this._testimonialSwiperEnabledBefore = swiperEl.enabled;
          }

          swiperEl.allowTouchMove = !locked;
          if ('enabled' in swiperEl) swiperEl.enabled = !locked;
        };

        // When modal opens/closes, temporarily disable Swiper interactions.
        if (modalEl) {
          modalEl.addEventListener('modal:handleAfterShow', () => {
            this._testimonialModalOpen = true;
            setSwiperLocked(true);
          });
          modalEl.addEventListener('modal:handleAfterHide', () => {
              // Delay unlock slightly to avoid a "close swipe" gesture being interpreted,
              // and to keep focusin-based slide changes disabled until after the close.
              setTimeout(() => {
                if (this._testimonialSwiperAllowTouchMoveBefore !== undefined) {
                  swiperEl && (swiperEl.allowTouchMove = this._testimonialSwiperAllowTouchMoveBefore);
                }
                if (
                  this._testimonialSwiperEnabledBefore !== undefined &&
                  swiperEl &&
                  'enabled' in swiperEl
                ) {
                  swiperEl.enabled = this._testimonialSwiperEnabledBefore;
                }

                this._testimonialModalOpen = false;

                this._testimonialSwiperAllowTouchMoveBefore = undefined;
                this._testimonialSwiperEnabledBefore = undefined;
              }, 200);
          });

          // Prevent overlay/close pointer interactions from affecting the underlying slider.
          modalEl.addEventListener(
            'pointerdown',
            (event) => {
              const t = event.target;
              if (!t?.closest) return;
              if (t.closest('.drawer__close-btn') || t.closest('.fixed-overlay')) {
                event.stopPropagation();
              }
            },
            true
          );
        }

        const openModalForButton = (clickedBtn) => {
          const modalBody = getModalBody();
          if (!modalBody) return;

          const testimonialEl = clickedBtn.closest('.testimonial');
          const readMoreContentEl = testimonialEl?.querySelector('.testimonial__readmore-content');
          if (!readMoreContentEl) return;

          modalBody.innerHTML = readMoreContentEl.innerHTML;
          modalEl?.show?.(clickedBtn);
          setSwiperLocked(true);
        };

        // Stop Swiper from reacting to pointer interactions on the button.
        this.addEventListener(
          'pointerdown',
          (event) => {
            const clickedBtn = event.target.closest('.testimonial__readmore');
            if (!clickedBtn) return;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );

        this.addEventListener(
          'click',
          (event) => {
            const clickedBtn = event.target.closest('.testimonial__readmore');
            if (!clickedBtn) return;
            event.preventDefault();
            event.stopPropagation();
            openModalForButton(clickedBtn);
          },
          true
        );

        this.addEventListener(
          'keydown',
          (event) => {
            const clickedBtn = event.target.closest?.('.testimonial__readmore');
            if (!clickedBtn) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            openModalForButton(clickedBtn);
          },
          true
        );
      }

      testimonialCards.forEach((testimonialEl) => {
        const previewEl = testimonialEl.querySelector('.testimonial__content--clamp');
        const readMoreBtn = testimonialEl.querySelector('.testimonial__readmore');
        const readMoreContentEl = testimonialEl.querySelector('.testimonial__readmore-content');

        if (!previewEl || !readMoreBtn) return;

        const clampLines = parseInt(previewEl.dataset.testimonialClampLines || '4', 10) || 4;

        // Default to hidden; we show only if content truly exceeds the clamp.
        readMoreBtn.hidden = true;

        // Measure using an unclamped clone (more reliable than scrollHeight with line-clamp).
        const cloneEl = previewEl.cloneNode(true);
        cloneEl.classList.remove('testimonial__content--clamp');
        cloneEl.style.position = 'absolute';
        cloneEl.style.visibility = 'hidden';
        cloneEl.style.pointerEvents = 'none';
        cloneEl.style.left = '-9999px';
        cloneEl.style.top = '0';
        cloneEl.style.width = `${previewEl.getBoundingClientRect().width}px`;
        cloneEl.style.display = 'block';
        cloneEl.style.overflow = 'visible';

        // Remove line clamp via inline styles (in case it's set by CSS).
        cloneEl.style.webkitLineClamp = 'unset';

        previewEl.parentElement.appendChild(cloneEl);

        const previewHeight = previewEl.getBoundingClientRect().height;
        const cloneHeight = cloneEl.getBoundingClientRect().height;
        const computedStyle = window.getComputedStyle(previewEl);

        let lineHeight = parseFloat(computedStyle.lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
          // Fallback: approximate line height from the preview box.
          lineHeight = previewHeight / clampLines;
        }

        // Epsilon in pixels used to avoid showing the button due to sub-pixel rounding.
        const epsilonPx = Math.max(2, lineHeight * 0.15);

        // 1) Best signal: if the content is clipped, scrollHeight will be larger than clientHeight.
        const scrollDiff = (previewEl.scrollHeight || 0) - (previewEl.clientHeight || 0);
        const isClippedByScroll = scrollDiff > epsilonPx;

        // 2) Fallback signal: compare the unclamped clone height to the expected clamp height.
        const expectedMaxHeight = lineHeight * clampLines;
        const isMoreThanClampByHeight = cloneHeight > expectedMaxHeight + epsilonPx;

        // Show "Read more" only when it's clearly more than the allowed number of lines.
        const isClamped = isClippedByScroll || isMoreThanClampByHeight;
        cloneEl.remove();

        if (isClamped) readMoreBtn.hidden = false;

        // No per-card event listeners: modal opening is handled via delegated handler above
        // to avoid Swiper loop-clone issues.
      });
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: columnGap.mobile,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.tabletItems),
            spaceBetween: columnGap.tablet
          },
          1024: {
            slidesPerView: parseInt(this.tabletLargeItems),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: true,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            if (this._testimonialModalOpen) return;
            if (this._testimonialModalEl && this._testimonialModalEl.hasAttribute('open')) return;
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }
      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('testimonials-component', Testimonials);
}
